/**
 * ProjectRax — i18n (Internationalization)
 * Full translation system for all screens.
 * Supports: English (en), Tamil (ta), Hindi (hi), Telugu (te), Malayalam (ml), Kannada (kn), Marathi (mr), Bengali (bn)
 * Syncs language preference with server (user.preferredLanguage) for cross-device persistence.
 *
 * Usage: const { t, lang, changeLang } = useI18n();
 *        <span>{t('att_title')}</span>
 *        <span>{t('an_lastDays', { n: 30 })}</span>
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';

import en from '../i18n/en.json';
import ta from '../i18n/ta.json';
import hi from '../i18n/hi.json';
import te from '../i18n/locales/te.json';
import ml from '../i18n/locales/ml.json';
import kn from '../i18n/locales/kn.json';
import mr from '../i18n/locales/mr.json';
import bn from '../i18n/locales/bn.json';
import { getAccessToken } from '../services/api';
import { userService } from '../services/dataService';
import authService from '../services/authService';

const translations = { en, ta, hi, te, ml, kn, mr, bn };

const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳' },
];

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('prax_lang') || 'en');

  // Sync language with server (fire-and-forget, non-blocking)
  const syncToServer = useCallback(async (code) => {
    try {
      if (!getAccessToken()) return;
      await userService.updateLanguage(code);
    } catch {
      // Silently fail — local preference is still saved
    }
  }, []);

  const changeLang = useCallback((code) => {
    setLang(code);
    localStorage.setItem('prax_lang', code);
    document.documentElement.lang = code;
    syncToServer(code);
  }, [syncToServer]);

  /**
   * Load language from server profile on mount (if user is logged in and has a preference)
   * This ensures cross-device sync: if user changed language on phone, desktop picks it up.
   */
  const loadFromServer = useCallback(async () => {
    try {
      if (!getAccessToken()) return;
      const res = await authService.getMe();
      const serverLang = res?.data?.data?.user?.preferredLanguage;
      if (serverLang && LANGUAGES.some(l => l.code === serverLang)) {
        // Server preference takes precedence over local
        setLang(serverLang);
        localStorage.setItem('prax_lang', serverLang);
        document.documentElement.lang = serverLang;
      }
    } catch {
      // Silently fail
    }
  }, []);

  // On mount, try to sync from server
  useEffect(() => {
    loadFromServer();
  }, [loadFromServer]);

  const t = useCallback((key, params) => {
    let text = translations[lang]?.[key] || translations.en?.[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, v);
      });
    }
    return text;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, changeLang, t, languages: LANGUAGES, loadFromServer }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

export { translations, LANGUAGES };
export default I18nProvider;
